import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {WifiIcon, ExclamationCircleIcon, CheckCircleIcon} from '@heroicons/react/outline';

const WARNING_DELAY = 3000;
const MANUAL_RETRY_DELAY = 15000;
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Presentation only: a feature supplies health and recovery, not another socket.
export default function LiveConnectionNotice({state, onReconnect, active = true}) {
    const [visible, setVisible] = useState(false);
    const [restored, setRestored] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const shown = useRef(false);
    const [retrying, setRetrying] = useState(false);
    const [retried, setRetried] = useState(false);
    const [canRetry, setCanRetry] = useState(false);
    const [manualExpired, setManualExpired] = useState(false);
    const retryInFlight = useRef(false);
    const [bottomClearance, setBottomClearance] = useState(0);
    const interrupted = ['offline', 'retrying', 'interrupted'].includes(state);
    useEffect(() => {
        if (!active || !interrupted) { setCanRetry(false); return; }
        const timer = setTimeout(() => setCanRetry(true), MANUAL_RETRY_DELAY);
        return () => clearTimeout(timer);
    }, [active, interrupted]);
    useEffect(() => {
        setManualExpired(false);
        if (!retried || !active || state === 'live') return;
        const timer = setTimeout(() => setManualExpired(true), MANUAL_RETRY_DELAY);
        return () => clearTimeout(timer);
    }, [retried, active, state === 'live']);
    useEffect(() => {
        setLeaving(false);
        if (!active) {
            shown.current = false;
            setVisible(false);
            setRestored(false);
            setRetried(false);
            return;
        }
        if (!interrupted) {
            if (state !== 'live') { setRestored(false); return; }
            if (!shown.current) return;
            setRestored(true);
            setRetried(false);
            const exitTimer = setTimeout(() => setLeaving(true), 2500);
            const timer = setTimeout(() => {
                shown.current = false;
                setVisible(false);
                setRestored(false);
            }, 2680);
            return () => { clearTimeout(timer); clearTimeout(exitTimer); };
        }
        setRestored(false);
        if (state === 'offline') {
            shown.current = true;
            setVisible(true);
            return;
        }
        if (shown.current) return;
        const timer = setTimeout(() => { shown.current = true; setVisible(true); }, WARNING_DELAY);
        return () => clearTimeout(timer);
    }, [interrupted, state === 'live', state === 'offline', active]);

    const target = restored ? 'restored' : state === 'offline' ? 'offline' : manualExpired ? 'refresh' : state === 'interrupted' ? (retried && !retrying ? 'refresh' : 'interrupted') : 'retrying';
    const [display, setDisplay] = useState(target);
    const [contentOut, setContentOut] = useState(false);
    const [height, setHeight] = useState(null);
    const content = useRef(null);
    useEffect(() => {
        if (!visible || reducedMotion()) { setDisplay(target); setContentOut(false); return; }
        if (display === target) { setContentOut(false); return; }
        setContentOut(true);
        const timer = setTimeout(() => { setDisplay(target); setContentOut(false); }, 120);
        return () => clearTimeout(timer);
    }, [target, visible, display]);
    useLayoutEffect(() => {
        if (!visible || !content.current) return;
        const measure = () => setHeight(content.current?.offsetHeight ?? null);
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(content.current);
        return () => observer?.disconnect();
    }, [visible, display, canRetry, retried]);
    useEffect(() => {
        if (!visible) return;
        let frame;
        const measure = () => {
            frame = null;
            let clearance = 0;
            if (window.innerWidth < 640) {
                document.querySelectorAll('[data-floating-notice-obstacle]').forEach(element => {
                    const rect = element.getBoundingClientRect();
                    if (rect.width && rect.top < window.innerHeight && rect.bottom >= window.innerHeight - 8) {
                        clearance = Math.max(clearance, window.innerHeight - rect.top);
                    }
                });
            }
            setBottomClearance(clearance);
        };
        const schedule = () => {
            if (frame == null) frame = requestAnimationFrame(measure);
        };
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        document.querySelectorAll('[data-floating-notice-obstacle]').forEach(element => observer?.observe(element));
        window.addEventListener('scroll', schedule, {passive: true});
        window.addEventListener('resize', schedule);
        // Re-measure after the existing sticky bar finishes sliding into view.
        document.addEventListener('transitionend', schedule);
        measure();
        return () => {
            if (frame != null) cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            document.removeEventListener('transitionend', schedule);
        };
    }, [visible]);
    if (!visible) return null;

    async function retry() {
        if (retryInFlight.current || retried) return;
        retryInFlight.current = true;
        setRetrying(true);
        setRetried(true);
        try { await onReconnect(); }
        catch (_) { /* The shared service owns recovery and its visible state. */ }
        finally { retryInFlight.current = false; setRetrying(false); }
    }

    const success = display === 'restored';
    const recovering = display === 'retrying';
    const Icon = success ? CheckCircleIcon : ['interrupted', 'refresh'].includes(display) ? ExclamationCircleIcon : WifiIcon;
    const titles = {offline: 'You’re Offline', retrying: 'Reconnecting to Live Updates', interrupted: 'Live Updates Interrupted', refresh: 'Refresh to Restore Updates', restored: 'Live Updates Restored'};
    const descriptions = {offline: 'We’ll reconnect when you’re back online.', retrying: 'Checking for the latest updates.', interrupted: 'Live updates are temporarily unavailable.', refresh: 'Refresh this page to restore live updates.', restored: 'You’re up to date.'};
    return <aside role="status" aria-live="polite" aria-atomic="true"
        data-live-connection-notice=""
        data-leaving={leaving}
        className={`live-connection-notice fixed z-40 left-4 right-4 mx-auto sm:left-auto sm:right-6 sm:mx-0 section-white rounded-theme border ${success ? 'border-emerald-200' : 'border-amber-300'} shadow-lg text-sm`}
        style={{width: '390px', maxWidth: 'calc(100% - 32px)', height: height ?? 'auto', '--connection-notice-clearance': `${bottomClearance}px`, bottom: 'calc(var(--connection-notice-clearance) + 1rem + env(safe-area-inset-bottom, 0px))'}}>
        <style>{`
            .live-connection-notice {overflow:hidden;transition:height 180ms ease,opacity 180ms ease,transform 180ms ease;animation:connection-notice-in 180ms ease-out;}
            .live-connection-notice[data-leaving=true] {opacity:0;transform:translateY(12px);pointer-events:none;}
            .live-connection-notice-content {transition:opacity 120ms ease,filter 120ms ease,transform 120ms ease;}
            .live-connection-notice-content[data-out=true] {opacity:0;filter:blur(3px);transform:translateY(-3px);pointer-events:none;}
            .live-connection-spinner {width:24px;height:24px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:connection-spinner-turn 800ms linear infinite;}
            @keyframes connection-notice-in {from {opacity:0;transform:translateY(12px)} to {opacity:1;transform:translateY(0)}}
            @keyframes connection-spinner-turn {to {transform:rotate(360deg)}}
            @media(prefers-reduced-motion:reduce) {.live-connection-notice,.live-connection-notice-content {animation:none;transition:none} .live-connection-spinner {animation:none}}
        `}</style>
        <div ref={content} className="live-connection-notice-content p-5 flex gap-3" data-out={contentOut}>
        <span className={`shrink-0 rounded-full w-10 h-10 flex items-center justify-center ${success ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} aria-hidden="true">
            {recovering ? <span className="live-connection-spinner" /> : <Icon className="w-6 h-6" />}
        </span>
        <div className="min-w-0 flex-1">
        <div className="font-semibold text-base">{titles[display]}</div>
        <div className="mt-1">{descriptions[display]}</div>
        {(['interrupted', 'refresh'].includes(display) || (recovering && canRetry)) && <button type="button" disabled={contentOut || target !== display || (display !== 'refresh' && (retrying || retried))} className="button mt-3" onClick={display === 'refresh' ? () => window.location.reload() : retry}>
            {display === 'refresh' ? 'Refresh Page' : retried || retrying ? 'Trying Again…' : 'Try Again'}
        </button>}
        </div></div>
    </aside>;
}
